import {
  TextField,
  TextInput,
  DateField,
  DateInput,
  SelectInput,
  SelectField,
  ReferenceInput,
  AutocompleteInput,
  ArrayField,
  SingleFieldList,
  FunctionField,
  useGetList,
  useNotify,
} from 'react-admin';
import { Chip } from '@mui/material';

import { Center } from 'src/components/Center';
import { CraftList, useCraftList } from 'src/components/CraftList';
import { dateParser } from 'src/utils/dateUtil';
import { VALIDATION_STATES, PUBLICATION_STATES } from 'src/utils/defaults/Constants';
import { TopicMetadata } from '../topicsMetadata/TopicMetadataTypes';
import { Signal } from './types';

export const SignalsList = () => {
  const list = useCraftList();
  const notify = useNotify();

  const {
    data: datasetTopicsData,
    isLoading: datasetTopicsLoading,
    error: datasetTopicsError,
  } = useGetList<TopicMetadata>('topics_metadata', { filter: { topic: 'dataset:' } });
  const datasetTopicsMetadataChoices = genTopicSelectOptions(datasetTopicsData);

  const {
    data: classTopicsData,
    isLoading: classTopicsLoading,
    error: classTopicsError,
  } = useGetList<TopicMetadata>('topics_metadata', { filter: { topic: 'class:' } });
  const classTopicMetadataChoices = genTopicSelectOptions(classTopicsData);

  if (datasetTopicsError || classTopicsError) {
    notify('There was a problem loading topics data. Please refresh to try again.', {
      type: 'error',
    });
  }

  const SignalsFilters = [
    <TextInput key="idFilter" label="Id" source="id@_eq" alwaysOn />,
    <DateInput key="dateFrom" label="Newer than" source="date@_gte" alwaysOn parse={dateParser} />,
    <DateInput key="dateTo" label="Older than" source="date@_lte" alwaysOn parse={dateParser} />,
    <SelectInput
      key="validationState"
      label="Validation State"
      source="validation_state@_eq"
      alwaysOn
      choices={VALIDATION_STATES}
    />,
    <SelectInput
      key="publicationState"
      label="Publication State"
      source="publication_state@_eq"
      alwaysOn
      choices={PUBLICATION_STATES}
    />,
    <AutocompleteInput
      key="datasetFilter"
      source="dataset"
      alwaysOn
      choices={datasetTopicsMetadataChoices}
      isLoading={datasetTopicsLoading || classTopicsLoading}
    />,
    <ReferenceInput
      key="clientsFilter"
      reference="signals_clients"
      source="clients@_contains"
      alwaysOn
      sort={{ field: 'client', order: 'ASC' }}
    >
      <AutocompleteInput
        label="Client"
        optionText="client"
        source="client"
        filterToQuery={(q: string) => ({ 'client@_ilike': q })}
        sx={{
          minWidth: '15rem',
        }}
      />
    </ReferenceInput>,
  ];

  return (
    <CraftList
      ref={list}
      title="Signals"
      filters={SignalsFilters}
      dataGridProps={{ empty: <Center sx={{ p: 2 }}>No signals found</Center> }}
    >
      <TextField source="id" />
      <TextField label="Title" source="signal_title" />
      <TextField label="Text" source="signal_text" />
      <DateField source="date" />
      <FunctionField
        label="Dataset"
        render={(record: Signal) => (
          <Chip
            label={datasetTopicsMetadataChoices.find((choice) => choice.id === record.dataset)?.name ?? record.dataset}
            onClick={() => {
              setListFilters(list, { dataset: record.dataset });
            }}
          />
        )}
      />
      <SelectField source="signal_class" label="Class" choices={classTopicMetadataChoices} />
      <ArrayField source="clients">
        <SingleFieldList linkType={false} sx={{ gap: 1 }}>
          <FunctionField
            render={(record: string) => (
              <Chip
                label={record}
                onClick={() => {
                  setListFilters(list, { 'clients@_contains': record });
                }}
              />
            )}
          />
        </SingleFieldList>
      </ArrayField>
      <SelectField source="validation_state" label="Validation State" choices={VALIDATION_STATES} />
      <TextField source="validated_by" label="Validated By" />
      <SelectField source="publication_state" label="Publication State" choices={PUBLICATION_STATES} />
    </CraftList>
  );
};

export default SignalsList;

function genTopicSelectOptions(topics?: TopicMetadata[]) {
  if (!topics) return [];

  return (
    topics
      ?.map((topicMetadata) => ({
        id: topicMetadata.topic.split(':')[1],
        name:
          topicMetadata.topic !== 'dataset:news_events'
            ? topicMetadata?.metadata?.name ?? topicMetadata.topic.split(':')[1].replace(/_/g, ' ')
            : 'News Events',
      }))
      .filter((topicMetadata) => topicMetadata.id !== '*')
      .sort((a, b) => a.name.localeCompare(b.name)) ?? []
  );
}

function setListFilters(listRef: ReturnType<typeof useCraftList>, filterData: Record<string, unknown>) {
  listRef.current?.setFilters(
    {
      ...listRef.current.filterValues,
      ...filterData,
    },
    listRef.current.displayedFilters,
  );
}
